import { Base, Divider, Expand, Flex, Icon, Link, Text } from '@bitrise/bitkit';
import { CrashEventThread, SupportedPlatforms } from '@trace/types';
import React, { Fragment, FunctionComponent, useState } from 'react';
import dataAtIds from '../../../../dataAtIds.json';
import { trackEvent } from '../../../../utils/tracking';

import CrashThread from './CrashThread';

interface Props {
  platform: SupportedPlatforms;
  threads: CrashEventThread[];
}

const CrashThreads: FunctionComponent<Props> = ({ platform, threads }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  /**
   * Handler fired each time the user expands the non-critical threads section.
   *
   * @param {boolean} isExpanded
   * @returns void
   */
  const handleExpandThreads = () => {
    setIsExpanded(!isExpanded);

    if (!isExpanded) {
      trackEvent('IssueDetails.Threads.Expand', {});
    }
  };

  return (
    <Base paddingHorizontal="x8">
      <Flex
        alignChildrenVertical="middle"
        clickable
        data-trace-at-id={dataAtIds.CrashPageEventThreads}
        direction="horizontal"
        gap="x4"
        onClick={handleExpandThreads}
      >
        <Flex
          alignChildrenVertical="middle"
          direction="horizontal"
          gap="x2"
          grow
          initial="none"
          paddingVertical="x4"
        >
          <Flex>
            <Icon name="TestSuccess" textColor="grape-5" />
          </Flex>

          <Flex grow initial="none">
            <Text monospace size="x2">
              {threads.length} non-critical threads
            </Text>
          </Flex>
        </Flex>

        <Flex>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link color="grape-3">
            <Text config="8">
              <Flex alignChildrenVertical="middle" direction="horizontal" gap="x1">
                <Flex>{isExpanded ? 'Hide' : 'Show'}</Flex>

                <Flex>
                  <Icon name={isExpanded ? 'MinusClose' : 'PlusOpen'} />
                </Flex>
              </Flex>
            </Text>
          </Link>
        </Flex>
      </Flex>

      <Expand expanded={isExpanded}>
        <Divider color="gray-2" width="1px" />

        <Flex direction="horizontal" gap="x10" paddingHorizontal="x2" paddingVertical="x4">
          <Flex>
            <Divider color="gray-5" direction="vertical" width="1px" />
          </Flex>

          <Flex grow>
            {threads.map((thread, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                {index !== 0 && <Divider color="gray-2" width="1px" />}
                <CrashThread index={index} platform={platform} {...thread} />
              </Fragment>
            ))}
          </Flex>
        </Flex>
      </Expand>
    </Base>
  );
};

export default CrashThreads;
